import React, { useState } from 'react'
import CustomBadge from '../../../Common/Badge/CustomBadge'
import { badgeColors } from '../../../Common/Color/Color'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material'

interface TaskBadgeProps {
  value: string
  badgeType: 'statusBadge' | 'importanceBadge'
  onChange?: (newValue: string) => void
}

const TaskBadge: React.FC<TaskBadgeProps> = ({ value, badgeType, onChange }) => {
  const importanceMapping: Record<string, { color: string; label: string }> = {
    Required: { color: badgeColors.red, label: 'Required' },
    Optional: { color: badgeColors.brightBlue, label: 'Optional' },
  }

  const statusMapping: Record<string, { color: string; label: string }> = {
    notStarted: { color: badgeColors.purpleEminence, label: 'Not Started' },
    inProgress: { color: badgeColors.burntOrange, label: 'In Progress' },
    completed: { color: badgeColors.greenSanFelix, label: 'Completed' },
  }

  const [selectedValue, setSelectedValue] = useState<string>(value)

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value
    
    // For status badges, don't update the local state immediately
    // Let the parent component handle validation and update
    if (badgeType === 'statusBadge') {
      if (onChange) {
        onChange(newValue)
      }
      // Don't update selectedValue here - let parent component control it
    } else {
      setSelectedValue(newValue)
      if (onChange) {
        onChange(newValue)
      }
    }
  }

  if (badgeType === 'statusBadge') {
    // Use the value prop instead of selectedValue for status badges
    // If status is completed, only show completed option
    const availableStatuses = value === 'completed' 
      ? ['completed'] 
      : Object.keys(statusMapping)

    return (
      <FormControl
        variant='outlined'
        margin='none'
        size='small'
        sx={{
          boxShadow: 'inset -10px 0px 23px -9px rgba(128,128,128,0.50)',
          borderRadius: 5,
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      >
        <Select
          value={badgeType === 'statusBadge' ? value || '' : selectedValue || ''}
          onChange={handleChange}
          renderValue={(selected) => (
            <CustomBadge
              label={statusMapping[selected]?.label || selected}
              size={fontSizes.size12}
              color={statusMapping[selected]?.color || badgeColors.warmYellow}
              width={80}
              rounded
            />
          )}
        >
          {availableStatuses.map((status) => (
            <MenuItem key={status} value={status}>
              <CustomBadge
                label={statusMapping[status]?.label || status}
                size={fontSizes.size12}
                color={statusMapping[status]?.color || badgeColors.warmYellow}
                width={80}
                rounded
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (badgeType === 'importanceBadge') {
    const importance = importanceMapping[value]
    return (
      <CustomBadge
        label={importance?.label || value}
        size={fontSizes.size12}
        color={importance?.color || badgeColors.warmYellow}
        width={80}
        rounded
      />
    )
  }

  return null
}

export default TaskBadge
